import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

type Review = {
  id: string;
  created_at: string;
  reviewer_name: string;
  excerpt: string;
  rating: number;
  google_review_url: string;
  avatar_url: string | null;
  publish: boolean;
  display_order: number;
};

const Reviews = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingReview, setEditingReview] = useState<Review | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    reviewer_name: "",
    excerpt: "",
    rating: 5,
    google_review_url: "",
    avatar_url: "",
    publish: true,
    display_order: 0,
  });

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabase
        .from("reviews")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      setReviews(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to fetch reviews",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (review?: Review) => {
    if (review) {
      setEditingReview(review);
      setFormData({
        reviewer_name: review.reviewer_name,
        excerpt: review.excerpt,
        rating: review.rating,
        google_review_url: review.google_review_url,
        avatar_url: review.avatar_url || "",
        publish: review.publish,
        display_order: review.display_order,
      });
    } else {
      setEditingReview(null);
      setFormData({
        reviewer_name: "",
        excerpt: "",
        rating: 5,
        google_review_url: "",
        avatar_url: "",
        publish: true,
        display_order: reviews.length,
      });
    }
    setIsDialogOpen(true);
  };

  const formatDbError = (error: any) => {
    const parts = [error?.message, error?.details, error?.hint].filter(Boolean);
    return parts.length ? parts.join(" — ") : "Unknown error";
  };

  const normalizePayload = () => {
    const reviewer_name = formData.reviewer_name.trim();
    const excerpt = formData.excerpt.trim();
    const google_review_url = formData.google_review_url.trim();
    const avatar_url = formData.avatar_url.trim();

    const ratingRaw = Number.isFinite(formData.rating) ? formData.rating : 5;
    const rating = Math.max(1, Math.min(5, Math.round(ratingRaw)));

    const displayOrderRaw = Number.isFinite(formData.display_order) ? formData.display_order : 0;
    const display_order = Math.max(0, Math.round(displayOrderRaw));

    return {
      reviewer_name,
      excerpt,
      rating,
      google_review_url,
      avatar_url: avatar_url.length ? avatar_url : null,
      publish: !!formData.publish,
      display_order,
    };
  };

  const validatePayload = (payload: ReturnType<typeof normalizePayload>) => {
    if (!payload.reviewer_name) return "Reviewer name is required.";
    if (!payload.excerpt) return "Review excerpt is required.";
    if (!payload.google_review_url) return "Google review URL is required.";
    if (!/^https?:\/\//i.test(payload.google_review_url)) return "Google review URL must start with http:// or https://.";
    if (!Number.isFinite(payload.rating) || payload.rating < 1 || payload.rating > 5) return "Rating must be between 1 and 5.";
    if (!Number.isFinite(payload.display_order) || payload.display_order < 0) return "Display order must be 0 or higher.";
    return null;
  };

  const handleSubmit = async () => {
    const payload = normalizePayload();
    const validationError = validatePayload(payload);

    if (validationError) {
      toast({
        title: "Fix required fields",
        description: validationError,
        variant: "destructive",
      });
      return;
    }

    try {
      if (editingReview) {
        const { error } = await supabase
          .from("reviews")
          .update(payload)
          .eq("id", editingReview.id);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Review updated successfully",
        });
      } else {
        const { error } = await supabase.from("reviews").insert(payload);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Review added successfully",
        });
      }

      setIsDialogOpen(false);
      fetchReviews();
    } catch (error: any) {
      console.error("Review save failed:", error);
      toast({
        title: "Error",
        description: formatDbError(error),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const { error } = await supabase.from("reviews").delete().eq("id", deleteId);

      if (error) throw error;
      toast({
        title: "Success",
        description: "Review deleted successfully",
      });
      fetchReviews();
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to delete review",
        variant: "destructive",
      });
    } finally {
      setDeleteId(null);
    }
  };

  const submitDisabledReason = isDialogOpen ? validatePayload(normalizePayload()) : null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin/dashboard")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold text-foreground">Manage Reviews</h1>
          </div>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Add Review
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Customer Reviews</CardTitle>
            <CardDescription>
              Manage testimonials and reviews displayed on your homepage
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No reviews found</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order</TableHead>
                    <TableHead>Reviewer</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Excerpt</TableHead>
                    <TableHead>Published</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviews.map((review) => (
                    <TableRow key={review.id}>
                      <TableCell>{review.display_order}</TableCell>
                      <TableCell className="font-medium">{review.reviewer_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: review.rating }).map((_, i) => (
                            <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{review.excerpt}</TableCell>
                      <TableCell>{review.publish ? "Yes" : "No"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(review)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleteId(review.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingReview ? "Edit Review" : "Add Review"}</DialogTitle>
            <DialogDescription>
              {editingReview ? "Update review information" : "Add a new customer review"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="reviewer_name">Reviewer Name</Label>
              <Input
                id="reviewer_name"
                value={formData.reviewer_name}
                onChange={(e) => setFormData({ ...formData, reviewer_name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="rating">Rating (1-5)</Label>
              <Input
                id="rating"
                type="number"
                min="1"
                max="5"
                value={formData.rating}
                onChange={(e) => {
                  const raw = e.target.value;
                  setFormData({ ...formData, rating: raw === "" ? 5 : Number(raw) });
                }}
              />
            </div>
            <div>
              <Label htmlFor="excerpt">Review Excerpt</Label>
              <Textarea
                id="excerpt"
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                rows={4}
              />
            </div>
            <div>
              <Label htmlFor="google_review_url">Google Review URL</Label>
              <Input
                id="google_review_url"
                value={formData.google_review_url}
                onChange={(e) => setFormData({ ...formData, google_review_url: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="avatar_url">Avatar Image URL</Label>
              <p className="text-sm text-muted-foreground mb-2">
                Paste the Google profile photo URL from the reviewer's Google account
              </p>
              <Input
                id="avatar_url"
                type="url"
                placeholder="https://lh3.googleusercontent.com/..."
                value={formData.avatar_url}
                onChange={(e) => setFormData({ ...formData, avatar_url: e.target.value })}
              />
              {formData.avatar_url && (
                <div className="mt-3 flex items-center gap-3">
                  <img 
                    src={formData.avatar_url} 
                    alt="Avatar preview" 
                    className="w-16 h-16 rounded-full object-cover border border-border"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <span className="text-sm text-muted-foreground">Preview</span>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="display_order">Display Order</Label>
              <Input
                id="display_order"
                type="number"
                value={formData.display_order}
                onChange={(e) => {
                  const raw = e.target.value;
                  setFormData({ ...formData, display_order: raw === "" ? 0 : Number(raw) });
                }}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="publish">Publish</Label>
              <Switch
                id="publish"
                checked={formData.publish}
                onCheckedChange={(checked) => setFormData({ ...formData, publish: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!!submitDisabledReason} title={submitDisabledReason ?? undefined}>
              {editingReview ? "Update" : "Add"} Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this review. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Reviews;
